import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import SectionTitle from "@/components/base/SectionTitle";
import { supabase } from "@/lib/supabase";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [humanVerified, setHumanVerified] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get("company_alt") as string;
    if (honeypot && honeypot.trim()) {
      setStatus("success");
      form.reset();
      setHumanVerified(false);
      return;
    }

    // Human verification check
    if (!humanVerified) {
      return;
    }

    setStatus("submitting");

    const payload = {
      name: (formData.get("name") as string) || "",
      company: (formData.get("company") as string) || "",
      email: (formData.get("email") as string) || "",
      phone: (formData.get("phone") as string) || "",
      subject: (formData.get("subject") as string) || "",
      message: (formData.get("message") as string) || "",
    };

    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: payload,
      });

      if (error) {
        setStatus("error");
        setErrorMsg("送信に失敗しました。再度お試しください。");
        return;
      }

      if (data && data.success) {
        setStatus("success");
        form.reset();
        setHumanVerified(false);
      } else {
        setStatus("error");
        setErrorMsg((data?.error as string) || "送信に失敗しました。再度お試しください。");
      }
    } catch {
      setStatus("error");
      setErrorMsg("ネットワークエラーが発生しました。再度お試しください。");
    }
  };

  const handleReset = () => {
    setStatus("idle");
    setHumanVerified(false);
    setErrorMsg("");
  };

  return (
    <PageLayout>
      <div className="relative py-14 md:py-20 bg-primary-700 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img
            src="https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/1dd01dbf-578c-47f9-a49f-e8acec76b216_compressed_IMG_5603.webp"
            alt="オフィス"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="mb-4">
              <ol className="flex items-center gap-2 text-sm text-background-300">
                <li>
                  <Link to="/" className="hover:text-background-100 transition-colors">
                    ホーム
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs" />
                </li>
                <li className="text-background-100">お問い合わせ</li>
              </ol>
            </nav>
            <h1 className="text-3xl md:text-4xl font-black text-background-50">お問い合わせ</h1>
            <p className="text-accent-300 text-sm mt-2">contact us</p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <SectionTitle
            title="お問い合わせフォーム"
            subtitle="下記フォームよりお気軽にお問い合わせください。内容を確認の上、担当者よりご連絡いたします。"
            centered
          />

          <div className="mt-8 bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
            {status === "success" ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-2xl text-accent-600" />
                </div>
                <h3 className="text-xl font-bold text-foreground-900 mb-2">送信完了</h3>
                <p className="text-sm text-foreground-600">
                  お問い合わせありがとうございます。担当者より折り返しご連絡いたします。
                </p>
                <button
                  onClick={handleReset}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
                >
                  新規入力
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                      お名前 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      placeholder="山田 太郎"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                      会社名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="company"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      placeholder="株式会社○○"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                      メールアドレス <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      placeholder="example@company.co.jp"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      placeholder="03-0000-0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    件名 <span className="text-primary-500">*</span>
                  </label>
                  <select
                    name="subject"
                    required
                    defaultValue=""
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent appearance-none"
                    style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.75rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.25rem" }}
                  >
                    <option value="" disabled>選択してください</option>
                    <option value="ガス腐食試験について">ガス腐食試験について</option>
                    <option value="受託環境試験について">受託環境試験について</option>
                    <option value="ドライアルファー（防錆処理）について">ドライアルファー（防錆処理）について</option>
                    <option value="ガス腐食試験機について">ガス腐食試験機について</option>
                    <option value="接触抵抗分析装置について">接触抵抗分析装置について</option>
                    <option value="採用情報について">採用情報について</option>
                    <option value="見積依頼">見積依頼</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    お問い合わせ内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-y"
                    placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                  />
                  <p className="text-xs text-foreground-500 mt-1 text-right">
                    最大500文字
                  </p>
                </div>

                {/* Human verification checkbox */}
                <div className="rounded-lg border border-background-300/60 bg-background-100 p-4">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        name="human_check"
                        checked={humanVerified}
                        onChange={(e) => setHumanVerified(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-5 h-5 rounded border-2 border-background-400 flex items-center justify-center transition-colors peer-checked:bg-accent-500 peer-checked:border-accent-500 peer-focus-visible:ring-2 peer-focus-visible:ring-accent-400 peer-focus-visible:ring-offset-1">
                        {humanVerified && (
                          <i className="ri-check-line text-xs text-background-50" />
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-foreground-700 leading-tight">
                      私はロボットではありません
                    </span>
                  </label>
                </div>

                {status === "error" && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                    {errorMsg}
                  </div>
                )}

                <div className="text-center pt-2">
                  <button
                    type="submit"
                    disabled={status === "submitting" || !humanVerified}
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        <span>送信中...</span>
                      </>
                    ) : (
                      <>
                        <span>送信する</span>
                        <i className="ri-send-plane-line" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Company info card */}
          <div className="mt-8 bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
            <div className="p-6 md:p-8">
              <h3 className="text-lg font-bold text-foreground-900 mb-6">お電話でのお問い合わせ</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                {/* 東京本社 */}
                <div className="p-5 md:border-r border-background-200/70">
                  <p className="text-xs font-semibold text-accent-600 mb-3 uppercase tracking-wide">
                    ガス腐食試験・受託環境試験に関してのご質問
                  </p>
                  <p className="text-sm font-bold text-foreground-900 mb-2">東京本社</p>
                  <p className="text-sm text-foreground-600 mb-1">〒146-0084 東京都大田区南久が原2-23-19</p>
                  <p className="text-sm text-foreground-600">
                    <a href="tel:0337572881" className="hover:text-primary-600 transition-colors">TEL 03(3757)2881</a>　FAX 03(3757)2861
                  </p>
                </div>

                {/* 横浜研究室 */}
                <div className="p-5 border-t md:border-t-0 border-background-200/70">
                  <p className="text-xs font-semibold text-secondary-600 mb-3 uppercase tracking-wide">
                    防錆処理(ドライアルファー)・ガス腐食試験機・接触抵抗分析装置に関してのご質問
                  </p>
                  <p className="text-sm font-bold text-foreground-900 mb-2">横浜研究室</p>
                  <p className="text-sm text-foreground-600 mb-1">〒246-0013 神奈川県横浜市瀬谷区相沢7-17-6</p>
                  <p className="text-sm text-foreground-600">
                    <a href="tel:0453014282" className="hover:text-secondary-600 transition-colors">TEL 045(301)4282</a>　FAX 045(301)9484
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 md:px-8 py-4 bg-background-50 border-t border-background-200/70">
              <Link
                to="/company"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
              >
                <i className="ri-building-line" />
                <span>会社案内を見る</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}